import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { ReactNode, useCallback, useMemo } from "react";
import { Label } from "reactstrap";
import {
  FACEBOOK_COLOR,
  INSTAGRAM_COLOR,
  LINKED_IN_COLOR,
  LINKED_IN_BUSINESS_COLOR,
  WHATSAPP_COLOR,
} from "../../../constants/socialColors.constant";
import { ConversationContact } from "../../../cores/socialChat/interfaces/conversations.interface";
import { getFirstLetters } from "../../utils/stringUtils";
import Avatar from "../Avatar";
import { PanelWembii } from "../PanelWembii";
import {
  BOARD_CONTACTS_MODAL,
  CONTACT_EXPAND_CREATION_FORM,
} from "../../../constants/modal.constant";
import { ContactSource } from "../../../cores/contacts/enum/ContactSource.enum";
import { Position } from "../../../cores/globals/interfaces/globalEntity";
import CreateContactForm from "../contacts/modal/CreateContactForm";
import { useMeCore } from "../../../cores/me";
import { useGlobalCore } from "../../../cores/globals";
import { useContactCore } from "../../../cores/contacts";
import { useSocialChatCore } from "../../../cores/socialChat";

interface Props {
  color: string;
  type: string;
  contacts: ConversationContact[];
  avatarAction?: (arg0: ConversationContact) => void;
  actions?: ReactNode;
  companyId: string;
  /** Lighter header bar for unified inbox layout */
  variant?: "default" | "inbox";
}

export default function PanelChatTitle(props: Props) {
  const { company } = useMeCore();
  const { updateConversationContact, conversationStatus } = useSocialChatCore();
  const { turnOnModal, turnOffModal } = useGlobalCore();
  const { getContactTags } = useContactCore();
  const { color, type, contacts, actions, avatarAction, variant = "default" } =
    props;
  const isInbox = variant === "inbox";
  const contactSocial =
    contacts[0].socials?.find((social) => social.socialName === type) ?? null;
  const getParticipantNames = useMemo(
    () => contacts.map((participant) => participant.name).join(", "),
    [contacts]
  );

  const onClickCreateContact = useCallback(() => {
    getContactTags(company?.id!);
    turnOnModal(
      CONTACT_EXPAND_CREATION_FORM,
      <CreateContactForm
        source={ContactSource.CHAT}
        preContactData={{
          externalReferenceId: contacts[0].id,
          name: getParticipantNames,
          reference: `Contacto proveniente de ${type}`,
          social: {
            socialName: type,
            socialUniqueId: (contacts[0].id ?? "").trim(),
            username: contacts[0].name,
          },
        }}
        onCreation={(contact) => {
          if (
            contact.socials![0].socialName === "facebook" ||
            contact.socials![0].socialName === "instagram" ||
            contact.socials![0].socialName === "linkedin" ||
            contact.socials![0].socialName === "linkedinBusiness"
          ) {
            updateConversationContact(
              {
                id: contact.id,
                name: contact.name,
                owners: contact.owners,
                avatarUrl: contacts[0].avatarUrl ?? "",
                email: contact.email,
                externalReferenceId: contact.externalReferenceId,
                from: contacts[0].from,
                state: `${contact.state}`,
                socials: contact.socials,
              },
              contact.socials![0].socialName
            );

            turnOffModal(BOARD_CONTACTS_MODAL);
          }
        }}
      />,
      Position.LEFT
    );
  }, [
    updateConversationContact,
    contacts,
    getParticipantNames,
    type,
    turnOffModal,
    turnOnModal,
    getContactTags,
    company?.id,
  ]);

  return (
    <PanelWembii
      shadow={!isInbox}
      className={cn(
        "mb-3 w-100",
        isInbox
          ? "panel-chat-title--inbox py-2 px-3 mb-0 bg-white text-dark border-bottom rounded-0"
          : "p-2 rounded-pill bg-primary text-light"
      )}
    >
      <div className="d-flex justify-content-between align-items-center w-100">
        <div className="d-flex align-items-center w-100">
          {type === "web" && (
            <FontAwesomeIcon
              className={cn({ "mr-2": isInbox })}
              icon={["fas", "globe"]}
              size={isInbox ? "sm" : "lg"}
              color="#64748b"
            />
          )}
          {type === "facebook" && (
            <FontAwesomeIcon
              className={cn({ "mr-2": isInbox })}
              icon={["fab", "facebook"]}
              size={isInbox ? "sm" : "lg"}
              color={isInbox ? FACEBOOK_COLOR : "#ffffff"}
            />
          )}
          {type === "instagram" && (
            <FontAwesomeIcon
              className={cn({ "mr-2": isInbox })}
              icon={["fab", "instagram"]}
              size={isInbox ? "sm" : "lg"}
              color={INSTAGRAM_COLOR}
            />
          )}
          {type === "whatsapp" && (
            <FontAwesomeIcon
              className={cn({ "mr-2": isInbox })}
              color={isInbox ? WHATSAPP_COLOR : "#ffffff"}
              icon={["fab", "whatsapp"]}
              size={isInbox ? "sm" : "lg"}
            />
          )}
          {type === "linkedin" && (
            <FontAwesomeIcon
              className={cn({ "mr-2": isInbox })}
              icon={["fab", "linkedin"]}
              size={isInbox ? "sm" : "lg"}
              color={LINKED_IN_COLOR}
            />
          )}
          {type === "linkedinBusiness" && (
            <FontAwesomeIcon
              className={cn({ "mr-2": isInbox })}
              icon={["fab", "linkedin"]}
              size={isInbox ? "sm" : "lg"}
              color={LINKED_IN_BUSINESS_COLOR}
            />
          )}
          <Avatar
            className={cn({
              "mx-2": !isInbox,
              "mr-2": isInbox,
              pointer: !!avatarAction && contactSocial?.socialUniqueId,
            })}
            label={getFirstLetters(contacts[0].name)}
            image={contacts[0].avatarUrl}
            backgroundColor={color}
            color={type === "instagram" ? "white" : "black"}
            width={isInbox ? "40px" : "45px"}
            height={isInbox ? "40px" : "45px"}
            style={{
              cursor:
                (type === "instagram" ||
                  type === "facebook" ||
                  type === "linkedin" ||
                  type === "linkedinBusiness") &&
                !contactSocial?.socialUniqueId
                  ? ""
                  : "pointer",
            }}
            onClick={
              (type === "instagram" ||
                type === "facebook" ||
                type === "linkedin" ||
                type === "linkedinBusiness") &&
              !contactSocial?.socialUniqueId
                ? () => null
                : () => avatarAction?.(contacts[0])
            }
          />
          <Label
            onClick={
              (type === "instagram" ||
                type === "facebook" ||
                type === "linkedin" ||
                type === "linkedinBusiness") &&
              !contactSocial?.socialUniqueId
                ? () => null
                : () => avatarAction?.(contacts[0])
            }
            className={cn(
              "font-weight-bold m-0 text-truncate d-none d-md-block",
              {
                "text-dark": isInbox,
                "h5 mb-0": !isInbox,
                "h6 mb-0": isInbox,
              }
            )}
            size="lg"
            style={{
              cursor:
                (type === "instagram" ||
                  type === "facebook" ||
                  type === "linkedin" ||
                  type === "linkedinBusiness") &&
                !contactSocial?.socialUniqueId
                  ? ""
                  : "pointer",
            }}
          >
            {getParticipantNames}
          </Label>
        </div>
        {actions && actions}

        {(type === "facebook" ||
          type === "instagram" ||
          type === "linkedin" ||
          type === "linkedinBusiness") &&
          contactSocial === null && (
            <button
              type="button"
              onClick={onClickCreateContact}
              className={cn(
                "btn rounded-pill border-0 bg-light text-primary",
                isInbox ? "p-1 px-2 shadow-sm" : "p-2 mx-2 shadow-lg"
              )}
            >
              <FontAwesomeIcon
                className={cn("text-primary", { "mx-1": !isInbox })}
                icon={["fas", "contact-book"]}
                size={isInbox ? "sm" : "lg"}
                color={conversationStatus !== "active" ? "grey" : ""}
              />
            </button>
          )}
      </div>
    </PanelWembii>
  );
}
